import styled from '@emotion/styled';
import { PlayArrowRounded } from '@mui/icons-material';
import { useEffect, useState } from 'react';
import { useLocation } from 'wouter';
import { getLiveTvChannelPath } from '~/modules/live-tv/liveTvRoutePaths';
import LiveTvBadge from './LiveTvBadge';
import { LiveTvChannelWithStream } from '../types/liveTv.types';

interface LiveTvChannelCardProps {
  channel: LiveTvChannelWithStream;
}

export default function LiveTvChannelCard({ channel }: LiveTvChannelCardProps) {
  const [, navigate] = useLocation();
  const [logoFailed, setLogoFailed] = useState(false);

  useEffect(() => {
    setLogoFailed(false);
  }, [channel.id, channel.logo]);

  const handleClick = () => {
    navigate(getLiveTvChannelPath(channel.id));
  };

  return (
    <Card type="button" onClick={handleClick}>
      <CardBody>
        {!logoFailed && channel.logo ? (
          <Logo
            key={`${channel.id}-${channel.logo}`}
            src={channel.logo}
            alt={channel.name}
            loading="lazy"
            decoding="async"
            onError={() => setLogoFailed(true)}
          />
        ) : (
          <LogoFallback>TV</LogoFallback>
        )}

        <Info>
          <Name>{channel.name}</Name>

          <Meta>
            {channel.country ?? 'Unknown country'}
            {channel.category ? ` · ${channel.category}` : ''}
          </Meta>

          <Badges>
            {channel.isOfficial && (
              <LiveTvBadge background="var(--walky-orange-soft)" color="var(--walky-orange)">
                Official
              </LiveTvBadge>
            )}

            {channel.geoRestriction && (
              <LiveTvBadge background="var(--walky-neutral-soft)" color="var(--walky-ink)">
                {channel.geoRestriction}
              </LiveTvBadge>
            )}
          </Badges>
        </Info>

        <PlayIcon aria-hidden="true">
          <PlayArrowRounded />
        </PlayIcon>
      </CardBody>
    </Card>
  );
}

const Card = styled.button`
  display: block;
  width: 100%;
  box-sizing: border-box;
  position: relative;
  min-height: 118px;
  text-align: left;
  border: 1px solid var(--walky-border);
  border-radius: 8px;
  padding: 16px;
  background: var(--walky-surface);
  color: var(--walky-ink);
  cursor: pointer;
  outline: none;
  box-shadow: 0 8px 22px rgba(24, 24, 27, 0.07);
  transition:
    transform 160ms ease,
    border-color 160ms ease,
    box-shadow 160ms ease;

  &:hover,
  &:focus-visible {
    transform: translateY(-2px);
    border-color: var(--walky-orange);
    box-shadow: 0 14px 30px rgba(24, 24, 27, 0.13);
  }
`;

const CardBody = styled.div`
  display: flex;
  gap: 14px;
  align-items: center;
  padding-right: 44px;

  @media (max-width: 680px) {
    padding-right: 0;
  }
`;

const Logo = styled.img`
  width: 68px;
  height: 68px;
  object-fit: contain;
  border-radius: 8px;
  background: var(--walky-logo-surface);
  border: 1px solid var(--walky-border);
  padding: 6px;
  flex-shrink: 0;
`;

const LogoFallback = styled.div`
  width: 68px;
  height: 68px;
  border-radius: 8px;
  background: var(--walky-charcoal);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 800;
  color: white;
  flex-shrink: 0;
`;

const Info = styled.div`
  min-width: 0;
  flex: 1 1 auto;
`;

const Name = styled.div`
  font-weight: 800;
  font-size: 16px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Meta = styled.div`
  color: var(--walky-muted);
  font-size: 14px;
  margin-top: 4px;
`;

const Badges = styled.div`
  margin-top: 10px;
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

const PlayIcon = styled.span`
  position: absolute;
  right: 14px;
  top: 50%;
  transform: translateY(-50%);
  width: 34px;
  height: 34px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  background: var(--walky-orange-soft);
  color: var(--walky-orange);
  transition:
    background 160ms ease,
    color 160ms ease;

  @media (max-width: 680px) {
    display: none;
  }

  ${Card}:hover &,
    ${Card}:focus-visible & {
    background: var(--walky-orange);
    color: white;
  }
`;
